import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { PlatformIcons } from './PlatformIcons';
import { textOnImageBody, textOnImageHeading } from '../theme/text-on-image';
import type { ReleaseEdit } from '../store/slices/admin-slice';

interface ReleaseItemPreviewProps {
  readonly values: ReleaseEdit;
}

/**
 * `<ReleaseItemPreview>` — live preview that mirrors a
 * `<ReleaseCard>` with the in-progress edit values. Captures the
 * 16:9 image-background hero shape, the title + body lockup, the
 * platform icons row, and the Purchase button so the admin can
 * verify the cover image / title fits the card before saving. The
 * Purchase button uses the FIRST entry in `purchaseLinks` (the rest
 * land on the Game-page detail surface).
 */
export function ReleaseItemPreview({ values }: ReleaseItemPreviewProps): JSX.Element {
  const heading = values.title || 'New release';
  const subtitle = values.body;
  const platforms = values.platforms;
  const image = values.image;
  const primaryPurchase = values.purchaseLinks.find((l) => l.url.trim().length > 0);
  return (
    <Card
      elevation={3}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        borderRadius: 2,
        color: 'common.white',
        backgroundImage: image ? `url(${image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: image ? 'transparent' : 'grey.900',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 75%, rgba(0,0,0,0.65) 100%)',
          pointerEvents: 'none',
        }}
      />
      <Stack
        spacing={1}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          padding: 2.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h3"
            component="h3"
            sx={{ ...textOnImageHeading, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {heading}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              sx={{
                ...textOnImageBody,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}
        >
          {platforms.length > 0 ? (
            <PlatformIcons platforms={platforms} iconSize={36} />
          ) : (
            <Box />
          )}
          {primaryPurchase && (
            <Button
              href={primaryPurchase.url}
              variant="contained"
              color="primary"
              size="medium"
              rel="sponsored noopener noreferrer"
              sx={{ minWidth: 0, height: 36 }}
            >
              {primaryPurchase.vendor ? `Buy on ${primaryPurchase.vendor}` : 'Purchase'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
